import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Spinner,
  Body1,
  Body2,
  Caption1,
} from '@fluentui/react-components';
import { ShieldRegular } from '@fluentui/react-icons';
import { useAuth } from '../features/authentication';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    minHeight: '100vh',
  },
  brandPanel: {
    flex: '1 1 55%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // 64px = spacingVerticalXXXL (32px) × 2; 48px = spacingHorizontalXXXL (32px) + spacingHorizontalL (16px).
    padding: `calc(${tokens.spacingVerticalXXXL} * 2) calc(${tokens.spacingHorizontalXXXL} + ${tokens.spacingHorizontalL})`,
    // #13B5B1 === brand primary — sourced from the token; #0a3d5c / #1ad4cf are
    // custom gradient shades with no exact brand-variant match, left as raw literals.
    background: `linear-gradient(135deg, #0a3d5c 0%, ${tokens.colorBrandBackground} 60%, #1ad4cf 100%)`,
    color: tokens.colorNeutralForegroundStaticInverted,
    position: 'relative',
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  brandLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
  },
  brandTitle: {
    // 42px: marketing display size outside Fluent's fontSize token set — left as-is.
    fontSize: '42px',
    fontWeight: 700,
    lineHeight: '1.1',
    letterSpacing: '-0.5px',
    margin: '0',
    color: tokens.colorNeutralForegroundStaticInverted,
  },
  brandTagline: {
    // 18px: no exact Fluent fontSize token (16/20 are the neighbours) — left as-is.
    fontSize: '18px',
    lineHeight: '1.5',
    opacity: '0.85',
    margin: '0',
  },
  brandFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    // 15px: no exact Fluent fontSize token (14/16 are the neighbours) — left as-is.
    fontSize: '15px',
    opacity: '0.9',
  },
  featureDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    // #95c43e (lime green) is not in brand-config.json's palette — no token
    // represents it, so it is left as a raw literal to avoid changing this accent.
    backgroundColor: '#95c43e',
    flexShrink: 0,
  },
  bgCircle1: {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    // rgba(255,255,255,0.04) === the static-inverted (white) token at ~4% alpha (hex 0A).
    backgroundColor: `${tokens.colorNeutralForegroundStaticInverted}0A`,
    // Decorative background-circle offsets — arbitrary aesthetic placement, not a
    // spacing-rhythm value; no clean token composition without shifting the design.
    top: '-100px',
    right: '-150px',
  },
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    // rgba(255,255,255,0.03) === the static-inverted (white) token at ~3% alpha (hex 08).
    backgroundColor: `${tokens.colorNeutralForegroundStaticInverted}08`,
    // Decorative background-circle offsets — left as-is (see bgCircle1).
    bottom: '-50px',
    left: '-80px',
  },
  loginPanel: {
    flex: '1 1 45%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // 48px = spacingVerticalXXXL (32px) + spacingVerticalL (16px); 40px = spacingHorizontalXXXL (32px) + spacingHorizontalS (8px).
    padding: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL}) calc(${tokens.spacingHorizontalXXXL} + ${tokens.spacingHorizontalS})`,
    backgroundColor: tokens.colorNeutralBackground1,
    '@media (max-width: 768px)': {
      flex: '1 1 100%',
    },
  },
  loginCard: {
    maxWidth: '380px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
  },
  loginHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  welcomeText: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    margin: '0',
  },
  loginBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
  description: {
    color: tokens.colorNeutralForeground2,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  signInButton: {
    width: '100%',
    height: '44px',
    // 15px: no exact Fluent fontSize token — left as-is.
    fontSize: '15px',
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    textAlign: 'center' as const,
  },
});

/**
 * Branded login page with LeftShield identity.
 * Split-panel layout: brand showcase (left) + login card (right).
 */
export function LoginPage() {
  const styles = useStyles();
  const { loginRedirect, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);


  const handleLogin = async () => {
    try {
      setError(null);
      await loginRedirect();
    } catch {
      setError('Sign in failed. Please try again.');
    }
  };

  return (
    <div className={styles.page}>
      {/* Left brand panel */}
      <div className={styles.brandPanel}>
        <div className={styles.bgCircle1} />
        <div className={styles.bgCircle2} />
        <div className={styles.brandContent}>
          <div className={styles.brandLogo}>
            {/* 56px: decorative hero icon size outside Fluent's fontSize token set — left as-is. */}
            <ShieldRegular style={{ fontSize: '56px', color: tokens.colorNeutralForegroundStaticInverted }} />
          </div>
          <h1 className={styles.brandTitle}>
            LeftShield
          </h1>
          <p className={styles.brandTagline}>
            AI-powered enterprise security and compliance platform. Protect your delivery pipeline with real-time enforcement.
          </p>
          <div className={styles.brandFeatures}>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>Runtime enforcement &amp; compliance</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>Code security scanning &amp; analysis</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>AI-powered risk insights</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>Evidence chain &amp; audit trails</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className={styles.loginPanel}>
        <div className={styles.loginCard}>
          <div className={styles.loginHeader}>
            <h2 className={styles.welcomeText}>Welcome back</h2>
            <Body1 className={styles.description}>
              Sign in to your LeftShield security portal
            </Body1>
          </div>

          <div className={styles.loginBody}>

            {error && (
              <Body2 className={styles.error}>
                {error}
              </Body2>
            )}

            <Button
              appearance="primary"
              size="large"
              className={styles.signInButton}
              onClick={handleLogin}
              disabled={isLoading}
              icon={isLoading ? <Spinner size="tiny" /> : undefined}
            >
              {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
            </Button>

          </div>

          <div className={styles.footer}>
            <Caption1 className={styles.description}>
              Secure authentication powered by Microsoft Azure AD
            </Caption1>
            <Caption1 className={styles.description}>
              By signing in, you agree to our Terms of Service and Privacy Policy.
            </Caption1>
          </div>
        </div>
      </div>
    </div>
  );
}
